import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router";
import { useWorkspace } from "@/shared/ui/WorkspaceContext";
import {
  resetHomeConversation,
  setHomeConversationId,
} from "@/features/intelligence/home/model/conversation";

export interface SimonConversationNavigation {
  startNewConversation: () => void;
  openConversation: (conversationId: string, options?: SimonNavigationOptions) => void;
  showHomeGreeting: (options?: SimonNavigationOptions) => void;
  closeConversation: () => void;
  showAllChats: () => void;
}

interface SimonNavigationOptions {
  replace?: boolean;
}

export function useSimonConversationNavigation(): SimonConversationNavigation {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { applicationVisible, systemPage, showChat, showSimonConversation, showAllChats } =
    useWorkspace();

  const startNewConversation = useCallback(() => {
    resetHomeConversation();
    showSimonConversation();
    if (pathname.startsWith("/chat")) {
      showChat();
      navigate("/");
      return;
    }
    showChat(applicationVisible && !systemPage);
  }, [applicationVisible, navigate, pathname, showChat, showSimonConversation, systemPage]);

  const openConversation = useCallback(
    (conversationId: string, options?: SimonNavigationOptions) => {
      setHomeConversationId(conversationId);
      showSimonConversation();
      if (pathname.startsWith("/chat")) {
        showChat();
        navigate("/", { replace: options?.replace });
        return;
      }
      showChat(applicationVisible && !systemPage, options);
    },
    [applicationVisible, navigate, pathname, showChat, showSimonConversation, systemPage],
  );

  const showHomeGreeting = useCallback(
    (options?: SimonNavigationOptions) => {
      resetHomeConversation();
      showSimonConversation();
      if (systemPage) {
        showChat(false, options);
        return;
      }
      showChat();
      navigate("/", { replace: options?.replace });
    },
    [navigate, showChat, showSimonConversation, systemPage],
  );

  const closeConversation = useCallback(() => {
    resetHomeConversation();
    showSimonConversation();
    showChat(true);
    if (pathname !== "/") navigate("/");
  }, [navigate, pathname, showChat, showSimonConversation]);

  return {
    startNewConversation,
    openConversation,
    showHomeGreeting,
    closeConversation,
    showAllChats,
  };
}
